import { Box, Typography, IconButton } from '@mui/material'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import UploadZone from '../UploadZone'
import Uppy from '@uppy/core'
import { useEffect, useState } from 'react'
import { ContainerParam, ContainerType, FileSystemItem } from '@features/subject/subject.types'
import {
  deleteItemsByIds,
  getAllChildrenIdsForParents,
  getUploadFileName,
  mapFilesToStructure,
} from '@features/subject/utils'
import { useParams } from 'react-router-dom'

type AddFilesStepProps = {
  uppy: Uppy
  containerParam: ContainerParam[]
  onCheckStepComplete: (bool: boolean) => void
}

const getFileTypeAllowed = (data: Array<ContainerParam>, type: ContainerType): string[] => {
  const container = data.find((item) => item.type === type)
  return container ? container.fileTypesAllowed : []
}

// upload types that can't be derived by the generic underscore-split rule
export const FILE_TYPE_EXTENSION_MAP: Record<string, string[]> = {
  VIDEO_MP4: ['.mp4'],
}

export const convertListFileTypes = (data: string[]): string[] => {
  const result = data.flatMap(
    (fileType) => FILE_TYPE_EXTENSION_MAP[fileType] ?? fileType.split('_').map((part) => `.${part.toLowerCase()}`),
  )

  // Add `.dcm` to the result by default
  if (!result.includes('.dcm')) {
    result.push('.dcm')
  }

  return result
}

export default function AddFilesStep({ uppy, containerParam, onCheckStepComplete }: AddFilesStepProps) {
  const [data, setData] = useState<FileSystemItem[]>(mapFilesToStructure(uppy.getFiles()))

  const { studyId, timepointSubmissionId, subjectId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId: string
  }>()

  const fileTypeAllowed = getFileTypeAllowed(containerParam, 'NON_DICOM')
  const listFileTypes = convertListFileTypes(fileTypeAllowed)

  const onRemove = (filesIds: string[]) => {
    const actualFiles = getAllChildrenIdsForParents(data, filesIds)
    uppy.removeFiles(actualFiles)
    setData(deleteItemsByIds(data, actualFiles))
  }

  const onDrop = (acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      const fileId = uppy.addFile(file)
      const relativePath =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        file.path || file.name

      uppy.setFileMeta(fileId, {
        path: relativePath,
        file_name: getUploadFileName(file.name, relativePath),
        file_type: file.type || 'application/octet-stream',
        study_id: studyId,
        subject_id: subjectId,
        timepoint_submission_id: timepointSubmissionId,
      })
    })

    setData(mapFilesToStructure(uppy.getFiles()))
  }

  // Handle folder selection using native file input
  const handleFolderSelect = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.webkitdirectory = true
    input.multiple = true
    input.style.display = 'none'

    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files
      if (files) {
        const fileArray = Array.from(files)

        fileArray.forEach((file) => {
          try {
            const relativePath = (file as File).webkitRelativePath || file.name
            const fileId = uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              meta: {
                relativePath,
                path: relativePath,
                file_name: getUploadFileName(file.name, relativePath),
                file_type: file.type || 'application/octet-stream',
                study_id: studyId,
                subject_id: subjectId,
                timepoint_submission_id: timepointSubmissionId,
              },
            })
            console.log('Added file:', file.name, 'with ID:', fileId)
          } catch (error) {
            console.error('Error adding file:', file.name, error)
          }
        })

        setData(mapFilesToStructure(uppy.getFiles()))
      }
      document.body.removeChild(input)
    }

    document.body.appendChild(input)
    input.click()
  }

  // Update data when files change
  useEffect(() => {
    const handleFileAdded = () => {
      setData(mapFilesToStructure(uppy.getFiles()))
    }

    const handleFileRemoved = () => {
      setData(mapFilesToStructure(uppy.getFiles()))
    }

    uppy.on('file-added', handleFileAdded)
    uppy.on('file-removed', handleFileRemoved)

    return () => {
      uppy.off('file-added', handleFileAdded)
      uppy.off('file-removed', handleFileRemoved)
    }
  }, [uppy])

  useEffect(() => {
    onCheckStepComplete(data.length > 0)
  }, [data.length])

  return (
    <Box>
      <Typography color="text.primary" variant="h4">
        Add and review files
      </Typography>
      <Typography color="text.secondary" variant="body2" mb={4}>
        Pick files or folders from your computer and add them here
      </Typography>

      {/* Enhanced UploadZone with integrated folder selection */}
      <Box position="relative">
        <UploadZone
          onRemove={onRemove}
          data={data}
          onDrop={onDrop}
          onDropRejected={() => {}}
          fileTypesAllowed={listFileTypes}
          accept={{ '*/*': [] }}
        />

        {/* Folder selection button positioned at top-right of dropzone */}
        <IconButton
          onClick={handleFolderSelect}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
            zIndex: 10,
          }}
          size="large"
          title="Select Folder"
          aria-label="Select folder for upload"
        >
          <FolderOpenIcon />
        </IconButton>
      </Box>
    </Box>
  )
}
